import {useNavigate, useParams} from "react-router-dom";
import React, {useCallback, useMemo, useState} from "react";
import PageBar from "../../component/PageBar";
import WorkspaceRow from "../workspace/WorkspaceRow";
import {useIntl} from "react-intl";
import {ChevronLeftIcon} from "@heroicons/react/24/outline";
import {Tab} from "@headlessui/react";
import UserDeleteAndRestoreTab from "./UserDeleteAndRestoreTab";
import useUser from "../../query/user/useUser";
import {Workspace} from "@/model/workspace";
import useWorkspaces from "@/query/workspace/useWorkspaces";

const pageCount = 10;
const UserDetail = () => {
    const intl = useIntl();
    const navigate = useNavigate();

    const { userId } = useParams<{userId: string}>();
    if (!userId) {
        throw Error("userId is not given.")
    }

    const { data: user } = useUser(userId);
    const [page, setPage] = useState<number>(1);
    const defaultPage = useMemo(() => {
        return page || 1
    }, [page])


    const {data: workspacePagination} = useWorkspaces({
        page: defaultPage,
        size: 10,
        userId: userId
    });

    const onClickPage = useCallback((page: number) => {
        setPage(page)
    }, [setPage])

    const lastPage = useMemo(() => {
        if (!workspacePagination) return 0;
        return Math.floor(workspacePagination.count / pageCount + ((workspacePagination.count % pageCount) && 1));
    }, [workspacePagination])

    const redirect = (workspace: Workspace) => {
        navigate(`/workspaces/${workspace._id}`,{state: {workspace: workspace}});
    }
    const onClickBack = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate(-1);
    };

    const tabClassName = (selected: boolean) => {
        return selected
            ? "transition-colors duration-200 text-gray-500 pb-3 focus:outline-none text-gray-900 border-b border-black font-bold"
            : "transition-colors duration-200 text-gray-500 pb-3 focus:outline-none"
    }

    if (!user) {
        return <></>
    }

    return (
        <div>
            <div className="m-5 px-2 pb-5 border-b-2 flex justify-between">
                <div className={"flex items-center text-2xl font-semibold"}>
                    <button  onClick={onClickBack}>
                        <ChevronLeftIcon className="w-6 h-6 mr-5"/>
                    </button>
                    {intl.formatMessage({id:"i11117"})}: {user.fullName} ({user.email})
                </div>
            </div>
            <div className="p-5">
                <Tab.Group defaultIndex={0}>
                    <Tab.List className="flex border-b space-x-5 mb-5">
                        <Tab className={({selected}) => tabClassName(selected)}>
                            {intl.formatMessage({id:"i11118"})}
                        </Tab>
                        <Tab className={({selected}) => tabClassName(selected)}>
                            {intl.formatMessage({id: "i11110"})}
                        </Tab>
                    </Tab.List>
                    <Tab.Panels>
                        <Tab.Panel>
                            <table className="w-full p-5 table-fixed break-keep sm:rounded-lg border-2">
                                <thead className="w-full text-sm font-bold text-left border-b bg-gray-200">
                                <tr className="text-[16px]">
                                    <th scope="col" className="p-5 py-4 text-left w-full">
                                        {intl.formatMessage({id: "i1001"})}
                                    </th>
                                    <th scope="col" className="p-2.5 py-4 text-left w-[10%]">
                                        {intl.formatMessage({id: "i11105"})}
                                    </th>
                                    <th scope="col" className="p-2.5 py-4 text-left w-[17%]">
                                        {intl.formatMessage({id: "i11106"})}
                                    </th>
                                    <th scope="col" className="p-2.5 py-4 text-left w-[25%]">
                                        {intl.formatMessage({id: "i1003"})}
                                    </th>
                                    <th scope="col" className="p-2.5 py-4 text-left w-[24%]">
                                        {intl.formatMessage({id: "i11104"})}
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                {workspacePagination?.workspaces.map((workspace) => {
                                    return (
                                        <WorkspaceRow
                                            key={workspace._id}
                                            workspace={workspace}
                                            redirect={()=>{redirect(workspace)}}
                                        />
                                    )
                                })}
                                </tbody>
                            </table>
                            <div>
                                <PageBar page={page} lastPage={lastPage} onClickPage={onClickPage}/>
                            </div>
                        </Tab.Panel>
                        <Tab.Panel>
                            <UserDeleteAndRestoreTab user={user}/>
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>
            </div>
        </div>
    )
}

export default UserDetail;
